import { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  MapPin, Clock, CheckCircle2, Circle, User, FileText,
  Radio, AlertTriangle, Navigation
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  assigned: { color: 'text-blue-400', bg: 'bg-blue-400/10', label: 'Awaiting Check-in', icon: Clock },
  in_progress: { color: 'text-[#A8E6CF]', bg: 'bg-[#A8E6CF]/10', label: 'In Progress', icon: Radio },
};

export default function ClientCareTracking() {
  const [data, setData] = useState({ today: '', active_shifts: [] });
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  const fetchData = async () => {
    try {
      const { data: res } = await axios.get(`${API_URL}/api/client-portal/care-tracking`, { withCredentials: true });
      setData(res);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    if (typeof window.L === 'undefined') return;

    const L = window.L;
    const map = L.map(mapRef.current).setView([51.5074, -0.1278], 13);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap'
    }).addTo(map);
    mapInstance.current = map;

    return () => { if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; } };
  }, [loading]);

  // Update markers
  useEffect(() => {
    if (!mapInstance.current || typeof window.L === 'undefined') return;
    const L = window.L;
    const map = mapInstance.current;

    // Clear existing markers
    map.eachLayer(layer => { if (layer instanceof L.Marker) map.removeLayer(layer); });

    const bounds = [];
    data.active_shifts.forEach(shift => {
      // Shift location
      if (shift.latitude && shift.longitude) {
        const marker = L.marker([shift.latitude, shift.longitude], {
          icon: L.divIcon({
            className: 'custom-div-icon',
            html: `<div style="background:#00A6A0;width:12px;height:12px;border-radius:50%;border:2px solid white;"></div>`,
            iconSize: [12, 12], iconAnchor: [6, 6]
          })
        }).addTo(map);
        marker.bindPopup(`<b>${shift.address || 'Shift Location'}</b><br/>${shift.start_time} - ${shift.end_time}`);
        bounds.push([shift.latitude, shift.longitude]);
      }

      // Live caregiver location
      if (shift.live_location?.latitude && shift.live_location?.longitude) {
        const cgMarker = L.marker([shift.live_location.latitude, shift.live_location.longitude], {
          icon: L.divIcon({
            className: 'custom-div-icon',
            html: `<div style="background:#FF6F61;width:14px;height:14px;border-radius:50%;border:2px solid white;box-shadow:0 0 8px rgba(255,111,97,0.5);"></div>`,
            iconSize: [14, 14], iconAnchor: [7, 7]
          })
        }).addTo(map);
        cgMarker.bindPopup(`<b>${shift.caregiver_name}</b><br/>Live Location`);
        bounds.push([shift.live_location.latitude, shift.live_location.longitude]);
      }

      // Check-in location
      if (shift.check_in_location?.latitude && shift.check_in_location?.longitude) {
        bounds.push([shift.check_in_location.latitude, shift.check_in_location.longitude]);
      }
    });

    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
    }
  }, [data.active_shifts]);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="client-care-tracking-page">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">Care Tracking</h1>
          <p className="text-sm text-muted-foreground mt-1">Real-time view of today's care delivery</p>
        </div>

        {/* Map */}
        <Card className="glass-card border-border overflow-hidden">
          <div ref={mapRef} style={{ height: '300px', width: '100%' }} data-testid="tracking-map" />
        </Card>

        {/* Active Shifts */}
        {data.active_shifts.length === 0 ? (
          <Card className="glass-card border-border">
            <CardContent className="p-8 text-center">
              <Clock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No active shifts today</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {data.active_shifts.map(shift => {
              const cfg = STATUS_CONFIG[shift.status] || STATUS_CONFIG.assigned;
              const StatusIcon = cfg.icon;
              return (
                <Card key={shift.id} className="glass-card border-border" data-testid={`shift-${shift.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      {/* Status indicator */}
                      <div className={`p-3 rounded-xl ${cfg.bg} flex-shrink-0`}>
                        <StatusIcon className={`w-6 h-6 ${cfg.color}`} />
                      </div>

                      <div className="flex-1 space-y-3">
                        {/* Caregiver info */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#00A6A0]/10 flex items-center justify-center overflow-hidden">
                              {shift.caregiver_photo ? (
                                <img src={shift.caregiver_photo} className="w-full h-full object-cover" alt="" />
                              ) : (
                                <User className="w-5 h-5 text-[#00A6A0]" />
                              )}
                            </div>
                            <div>
                              <p className="text-foreground font-medium">{shift.caregiver_name}</p>
                              <p className="text-xs text-muted-foreground">{shift.start_time} - {shift.end_time}</p>
                            </div>
                          </div>
                          <span className={`text-xs px-3 py-1 rounded-full ${cfg.bg} ${cfg.color} font-medium`}>
                            {cfg.label}
                          </span>
                        </div>

                        {/* Progress indicators */}
                        <div className="grid grid-cols-3 gap-3">
                          <div className={`p-2 rounded-lg text-center ${shift.check_in ? 'bg-[#A8E6CF]/10' : 'bg-muted/30'}`}>
                            {shift.check_in ? (
                              <CheckCircle2 className="w-4 h-4 text-[#A8E6CF] mx-auto mb-1" />
                            ) : (
                              <Circle className="w-4 h-4 text-muted-foreground mx-auto mb-1" />
                            )}
                            <p className="text-[10px] text-muted-foreground">Check-in</p>
                            {shift.check_in && (
                              <p className="text-[10px] text-[#A8E6CF]">
                                {new Date(shift.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            )}
                          </div>
                          <div className={`p-2 rounded-lg text-center ${shift.care_report_submitted ? 'bg-[#00A6A0]/10' : 'bg-muted/30'}`}>
                            {shift.care_report_submitted ? (
                              <FileText className="w-4 h-4 text-[#00A6A0] mx-auto mb-1" />
                            ) : (
                              <Circle className="w-4 h-4 text-muted-foreground mx-auto mb-1" />
                            )}
                            <p className="text-[10px] text-muted-foreground">Care Report</p>
                            {shift.care_report_signed && (
                              <p className="text-[10px] text-[#00A6A0]">Signed</p>
                            )}
                          </div>
                          <div className={`p-2 rounded-lg text-center ${shift.check_out ? 'bg-[#A8E6CF]/10' : 'bg-muted/30'}`}>
                            {shift.check_out ? (
                              <CheckCircle2 className="w-4 h-4 text-[#A8E6CF] mx-auto mb-1" />
                            ) : (
                              <Circle className="w-4 h-4 text-muted-foreground mx-auto mb-1" />
                            )}
                            <p className="text-[10px] text-muted-foreground">Check-out</p>
                          </div>
                        </div>

                        {/* Location */}
                        {shift.address && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <MapPin className="w-3 h-3 text-[#00A6A0]" />
                            {shift.address}
                          </div>
                        )}

                        {/* Live location indicator */}
                        {shift.live_location && (
                          <div className="flex items-center gap-2 text-xs text-[#FF6F61]">
                            <Navigation className="w-3 h-3 animate-pulse" />
                            Live GPS tracking active
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
